import api from '@/lib/api/axios'
import { authHeaders } from '@/features/ai-chat/core/utils/authHeaders'

// The long IaC model endpoints (/architecture, /validate) stream their result over SSE
// (with a heartbeat) so they survive CloudFront's 30s origin timeout no matter how long
// the model takes. Consume that stream and return the final result shaped like an axios
// response ({ data }) so existing callers (res.data) keep working unchanged.
async function streamJsonResult<T>(url: string, body: unknown): Promise<{ data: T }> {
    // Abort if the stream goes idle (no bytes) for 30s. The backend sends a ': keepalive'
    // heartbeat every 10s, so prolonged silence means it has genuinely hung (model stall,
    // dropped origin) — without this the await never settles and the UI spins forever. The
    // timer resets on every chunk, so a slow-but-alive stream is never killed.
    const controller = new AbortController()
    let idleTimer: ReturnType<typeof setTimeout> | undefined
    let timedOut = false
    const resetIdle = () => { if (idleTimer) clearTimeout(idleTimer); idleTimer = setTimeout(() => { timedOut = true; controller.abort() }, 30000) }
    let result: any = null
    let errorMsg: string | null = null
    // Some refusals are not worth retrying (a project file the server will not accept); the code
    // lets the caller say what to do about it instead of offering "press Verify again".
    let errorCode: string | null = null
    try {
        resetIdle()
        const res = await fetch(url, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
            signal: controller.signal,
        })
        // The endpoint must stream SSE. If the content-type is anything else, the
        // body is NOT events — almost always CloudFront serving index.html for a
        // masked 401/403 (expired session, or the WAF/threat-detector flagging the
        // Terraform content as XSS). Reading that HTML as a stream finds no `data:`
        // lines and surfaces a bogus "No result" + a stuck Verify. Detect it and
        // give an actionable error instead.
        if (!res.headers.get('content-type')?.includes('text/event-stream')) {
            if (!res.ok) throw new Error(`Request failed with status code ${res.status}`)
            throw new Error("Verification didn't return a stream — the request was redirected or blocked (often an expired session, or the security filter flagging the Terraform). Sign in again if needed, then press Verify to retry.")
        }
        const reader = res.body?.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let evt = '' // current SSE event type: 'result' | 'error' | 'done' | ''
        // Capture ONLY the 'result'/'error' event payloads. The backend ends EVERY stream
        // with a terminating `event: done\ndata: {}` — if that {} is treated as the result it
        // CLOBBERS the real one (last-data-wins). That is exactly what broke the architecture
        // diagram (and made validate parse as empty) after the move to SSE. Track the event
        // type and ignore 'done'; heartbeats (': keepalive'/': connected') never start with 'data:'.
        const consume = (dataLine: string) => {
            if (evt === 'done') return
            try {
                const d = JSON.parse(dataLine)
                if (evt === 'error' || (d && d.success === false && d.error)) { errorMsg = (d && d.error) || 'Request failed'; errorCode = d?.code || null }
                else result = d
            } catch { /* heartbeat / partial line */ }
        }
        if (reader) {
            while (true) {
                const { done, value } = await reader.read()
                if (done) break
                resetIdle() // bytes arrived — the stream is alive
                buffer += decoder.decode(value, { stream: true })
                const lines = buffer.split('\n')
                buffer = lines.pop() || ''
                for (const line of lines) {
                    if (line.startsWith('event: ')) evt = line.slice(7).trim()
                    else if (line.startsWith('data: ')) consume(line.slice(6))
                    else if (line === '') evt = '' // blank line terminates an SSE event
                }
            }
        }
        // The stream can close right after the final "data:" line WITHOUT its terminating
        // blank line (abrupt connection close, or the last chunk lands exactly on the data
        // line). The loop only processes lines followed by a "\n", so flush a trailing one
        // here — guarded by the same event type, so a 'done' event is never taken as result.
        if (!result && !errorMsg && buffer.startsWith('data: ')) consume(buffer.slice(6))
    } catch (e) {
        if (timedOut || (e instanceof Error && e.name === 'AbortError')) {
            throw new Error('The request timed out — the server stopped responding (no data for 30s). Please retry.')
        }
        throw e
    } finally {
        if (idleTimer) clearTimeout(idleTimer)
    }
    if (errorMsg) throw Object.assign(new Error(errorMsg), errorCode ? { code: errorCode } : {})
    if (!result) throw new Error('No result returned from server')
    return { data: result as T }
}

export interface IaCProject {
    id: number
    mainId: string
    name: string
    description: string | null
    cloudPlatform: string
    status: string
    summary: string | null
    createdAt: string
    updatedAt: string
    files?: IaCFile[]
}

export interface IaCFile {
    id: number
    projectId: number
    filePath: string
    content: string
    description: string | null
    version: number
    createdAt: string
    updatedAt: string
}

/** Where an object can be rebuilt, decided on the server by backend/src/lib/iac-placement.js. */
export type IaCPlacement = 'cloud' | 'cloud-native' | 'ibm-power' | 'recovery-site' | 'saas' | 'not-built' | 'review'

export interface IaCObjectPlacement {
    placement: IaCPlacement
    platform: string | null
    family: string | null
    source: string
    rule: string
    reason: string
    /** The placement question that decides it: 'ibm-z', 'ibm-power', 'midrange-unix', 'telephony', ... */
    group?: string
    /** A cloud-native object the recovery site also needs, for these on-site objects. */
    alsoAtSite?: string[]
}

export interface IaCContextObject {
    objectId: string
    description: string | null
    classification: { key: string; name: string; category: string } | null
    locationCategory: string | null
    placement: IaCObjectPlacement
    // No layerId any more (22 Oct 2026): Objects.LayerID named the wrong band for 280 objects and
    // nothing for the 93 OG-* ones. What an object is comes from its classification.
    isDatastore: boolean
    technologyDescription: string | null
    technologyModel: string | null
    technologyCategory: string | null
    objectService: string | null
    locationName: string | null
    versions: Array<{
        versionId: number
        type: string | null
        chainSet: string | null
        analytics: number | null
        size: number | null
        anomaly: number | null
        primaryChainCompromised: boolean
        vaultDatetime: string | null
        metadata: Record<string, any> | null
    }>
}

export interface IaCContext {
    mainId: string
    scenario: number
    serviceName: string
    objectCount: number
    versionCount: number
    hierarchyData: IaCContextObject[]
    dependencies: Array<{ source: string; target: string }>
    placement: {
        counts: Partial<Record<IaCPlacement, number>>
        migration: 'on-premises-to-cloud' | 'cloud-to-cloud' | 'hybrid' | 'unknown-source' | 'none'
        sourceClouds: string[]
        crossings: Array<{ provider: string; providerPlacement: string; dependent: string; dependentPlacement: string }>
        recoverySites: Array<{ id: number; name: string | null }>
        questions?: Array<{ id: string; ask: string; options: string[] }>
        equivalents: Record<string, { aws: string; azure: string; gcp: string; ibm: string }>
    }
}

export const iacApi = {
    // Get hierarchy context for IaC generation
    getContext: (mainId: string, scenario?: number) =>
        api.get<{ success: boolean; data: IaCContext }>(`/api/objects/${encodeURIComponent(mainId)}/iac-context`, {
            params: { scenario: scenario || 1 },
        }),

    // IaC list screen — one consolidated query (vw_IaCList view), one row per session
    list: () =>
        api.get<{ success: boolean; sessions: any[] }>('/api/ai/iac/list'),

    // Derive the architecture diagram ({containers, nodes, edges}) from the current Terraform.
    // This runs the model over all the .tf files; how long depends on the environment's
    // complexity, so there's no fixed timeout (timeout: 0) — consistent with the other AI
    // calls, which stream over fetch with no timeout. Result is cached server-side after.
    deriveArchitecture: (files: Array<{ filename: string; code: string }>, opts?: { projectId?: number | null; force?: boolean }) =>
        streamJsonResult<{ success: boolean; data: any; cached?: boolean; persisted?: boolean }>(
            '/api/ai/iac/architecture',
            { files, projectId: opts?.projectId ?? undefined, force: opts?.force ?? false },
        ),

    // Run `terraform validate` server-side and auto-fix errors via the model,
    // returning the (possibly corrected) files + the final validation result.
    // No timeout: terraform init can download providers on a cold cache.
    validate: (files: Array<{ filename: string; code: string }>, maxAttempts = 1) =>
        streamJsonResult<{
            success: boolean; ran: boolean; valid: boolean; attempts: number;
            errorCount: number; warningCount: number;
            diagnostics: Array<{ severity: string; summary: string; detail: string; file: string | null; line: number | null }>;
            files: Array<{ filename: string; code: string }>; changedFiles: string[]; message?: string;
            // Set when validate could not run because of a provider or a module source the
            // Terraform names — the Terraform itself has NOT been checked, so this is not a pass
            // and not a failure. `message` says which, and what this server will install.
            blocked?: 'provider-not-allowed' | 'module-source-not-allowed' | 'no-provider-allowlist';
        }>('/api/ai/iac/validate', { files, maxAttempts }),

    // Projects
    listProjects: (mainId?: string) =>
        api.get('/api/ai/iac/projects', { params: mainId ? { mainId } : {} }),

    getProject: (id: number) =>
        api.get(`/api/ai/iac/projects/${id}`),

    createProject: (data: { mainId: string; name: string; description?: string; cloudPlatform: string; scenario?: number; summary?: string; userId?: number }) =>
        api.post('/api/ai/iac/projects', data),

    updateProject: (id: number, data: { status?: string; name?: string; description?: string; summary?: string }) =>
        api.patch(`/api/ai/iac/projects/${id}`, data),

    deleteProject: (id: number) =>
        api.delete(`/api/ai/iac/projects/${id}`),

    // Files
    saveFile: (projectId: number, data: { filePath: string; content: string; description?: string }) =>
        api.post(`/api/ai/iac/projects/${projectId}/files`, data),

    updateFile: (fileId: number, data: { content?: string; description?: string }) =>
        api.put(`/api/ai/iac/files/${fileId}`, data),

    deleteFile: (fileId: number) =>
        api.delete(`/api/ai/iac/files/${fileId}`),

    // Remove a file from a project by PATH, not by row id: a project saved before the save became
    // an upsert can hold several versioned rows for one path, and deleting only the newest brings
    // the previous version back on the next load.
    deleteProjectFile: (projectId: number, filePath: string) =>
        api.delete(`/api/ai/iac/projects/${projectId}/files`, { params: { filePath } }),

    // The identity a project deploys as - never a secret (backend/src/lib/iac-deploy-target.js).
    getDeployTarget: (projectId: number, cloudProvider: IaCCloud) =>
        api.get<{ success: boolean; data: IaCDeployTarget | null }>(`/api/iac/projects/${projectId}/deploy-target/${cloudProvider}`),

    saveDeployTarget: (projectId: number, cloudProvider: IaCCloud, data: { region: string; identity: Record<string, string> }) =>
        api.put<{ success: boolean; data: IaCDeployTarget }>(`/api/iac/projects/${projectId}/deploy-target/${cloudProvider}`, data),

    deleteDeployTarget: (projectId: number, cloudProvider: IaCCloud) =>
        api.delete(`/api/iac/projects/${projectId}/deploy-target/${cloudProvider}`),

    // Deployments
    getDeployment: (projectId: number) =>
        api.get(`/api/iac/deployments/project/${projectId}`),

    createDeployment: (data: { projectId: number; cloudProvider: IaCCloud }) =>
        api.post('/api/iac/deployments', data),

    /** For destroy.sh: a fresh deploy token, the identity to sign in to and the engineer's session name. */
    prepareScript: (deploymentId: number) =>
        api.post<{ success: boolean; data: IaCDeployment }>(`/api/iac/deployments/${deploymentId}/script`),

    updateDeploymentStatus: (deploymentId: number, data: { status: string; statusMessage?: string; planOutput?: string; applyOutput?: string; destroyOutput?: string; outputs?: any }) =>
        api.put(`/api/iac/deployments/${deploymentId}/status`, data),
}

export interface IaCDeployment {
    id: number
    projectId: number
    cloudProvider: string
    region: string | null
    status: 'not_deployed' | 'pending' | 'planning' | 'planned' | 'deploying' | 'deployed' | 'destroying' | 'destroyed' | 'failed'
    statusMessage: string | null
    planOutput: string | null
    applyOutput: string | null
    destroyOutput: string | null
    deployedAt: string | null
    destroyedAt: string | null
    createdAt: string
    deployToken?: string
    /** On a new deployment: the identity the script signs in to (no secret). */
    target?: IaCDeployTarget
    /** On a new deployment: the engineer's name in the cloud's own audit log. */
    sessionName?: string
}

export type IaCCloud = 'aws' | 'azure' | 'gcp' | 'ibm'

/**
 * Which identity a project deploys as. No secret: the deploy script signs in as the engineer and
 * takes this identity for an hour.
 *   aws { roleArn } | azure { tenantId, subscriptionId } | gcp { projectId, serviceAccount } | ibm { accountId, resourceGroup? }
 */
export interface IaCDeployTarget {
    cloudProvider: IaCCloud
    region: string
    identity: Record<string, string>
    updatedAt?: string
}
